import { defineStore } from 'pinia'

interface Ticket {
  passenger_id: string
  passenger_type: string
  product_family: string
  price: number
  originalPrice: number
  travelIndex: number
  routeIndex: number
}

interface Seat {
  seat_number: string
  seat_property: string
  price: number
  originalPrice: number
}

interface Equipment {
  product_code: string
  product_type: string
  price: number
  originalPrice: number
  direction: 'outbound' | 'inbound'
}

export const usePriceStore = defineStore('price', {
  state: () => ({
    outboundTickets: [] as Ticket[],
    inboundTickets: [] as Ticket[],
    seats: [] as Seat[],
    equipment: [] as Equipment[],
    rebookingPrice: undefined as number | undefined,
  }),
  getters: {
    // Get all tickets (both outbound and inbound)
    tickets: (state) => [...state.outboundTickets, ...state.inboundTickets],
    
    // Get price for each passenger
    passengerPrice: (state) => (passengerId: string) => {
      const ticket = [...state.outboundTickets, ...state.inboundTickets].find(
        (ticket) => ticket.passenger_id === passengerId
      )
      return ticket?.price || 0
    },
    
    // Get total price for all passengers with the same passenger_type
    passengerTypePrice: (state) => (passengerType: string) => {
      return [...state.outboundTickets, ...state.inboundTickets]
        .filter((ticket) => ticket.passenger_type === passengerType)
        .reduce((total, ticket) => total + ticket.price, 0)
    },
    
    // Get total price for all passengers
    totalPassengerPrice: (state) => {
      return [...state.outboundTickets, ...state.inboundTickets].reduce(
        (total, ticket) => total + ticket.price,
        0
      )
    },
    
    // Get price for each selected seat
    seatPrice: (state) => (seatNumber: string) => {
      return (
        state.seats.find((seat) => seat.seat_number === seatNumber)?.price || 0
      )
    },
    
    // Get total price for all seats with the same property
    seatPropertyPrice: (state) => (seatProperty: string) => {
      return state.seats
        .filter((seat) => seat.seat_property === seatProperty)
        .reduce((total, seat) => total + seat.price, 0)
    },
    
    // Get total price for all selected seats
    totalSeatPrice: (state) => {
      return state.seats.reduce((total, seat) => total + seat.price, 0)
    },
    
    // Get total price for all selected equipment
    totalEquipmentPrice: (state) => {
      return state.equipment.reduce((total, item) => total + item.price, 0)
    },
    
    // Get total price of everything in the store
    totalPrice: (state) => {
      // Calculate total ticket price for both outbound and inbound
      const totalTicketPrice = [...state.outboundTickets, ...state.inboundTickets].reduce(
        (total, ticket) => total + ticket.price,
        0
      )

      // Calculate total seat price
      const totalSeatPrice = state.seats.reduce(
        (total, seat) => total + seat.price,
        0
      )

      // Calculate total equipment price
      const totalEquipmentPrice = state.equipment.reduce(
        (total, item) => total + item.price,
        0
      )

      // Return total price for all items
      return totalTicketPrice + totalSeatPrice + totalEquipmentPrice
    },
  },
  actions: {
    clear() {
      this.outboundTickets = []
      this.inboundTickets = []
      this.seats = []
      this.equipment = []
      this.rebookingPrice = undefined
    },
    
    updateSeat(seatNumber: string, seatProperty: string, price: number) {
      // Remove any existing seat with the same number
      this.seats = this.seats.filter((seat) => seat.seat_number !== seatNumber)

      // Add the new seat
      this.seats.push({
        seat_number: seatNumber,
        seat_property: seatProperty,
        price: price,
        originalPrice: price,
      })
    },
    
    removeSeatByNumber(seatNumber: string) {
      this.seats = this.seats.filter((seat) => seat.seat_number !== seatNumber)
    },
    
    addEquipment(product_code: string, product_type: string, price: number, direction: 'outbound' | 'inbound') {
      this.equipment.push({
        product_code,
        product_type,
        price,
        originalPrice: price,
        direction
      })
    },
    
    removeEquipmentByCode(product_code: string) {
      this.equipment = this.equipment.filter(
        (item) => item.product_code !== product_code
      )
    },
    
    addTicket(ticket: Ticket) {
      // Add ticket to the correct array based on travel index
      if (ticket.travelIndex === 0) {
        this.outboundTickets.push(ticket);
      } else {
        this.inboundTickets.push(ticket);
      }
    },
    
    setRebookingPrice(price: number) {
      this.rebookingPrice = price
    },
  },
})
